/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DateTimePicker.tsx - Selector Fecha/Hora Estilo Apple Premium
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 1.0.0
 * @description Picker elegante estilo hotel booking con animaciones suaves
 * @architecture Componente reutilizable para Scheduler
 * 
 * FEATURES:
 * - ✅ Calendario visual mensual (grid 7x6)
 * - ✅ Selector de hora con scroll picker iOS-style
 * - ✅ Zona horaria integrada
 * - ✅ Validación de fecha pasada
 * - ✅ Preview en tiempo real
 * - ✅ Animaciones suaves Apple-style
 * - ✅ Colores Aqua #2ca799
 * - ✅ Dark/Light mode compatible
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Card, Badge, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faClock,
  faCalendarAlt,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface DateTimePickerProps {
  value?: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  error?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isPast: boolean;
  isWeekend: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════════════════

const getDaysInMonth = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days: Date[] = [];
  
  // Días del mes anterior para llenar
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const prevDate = new Date(year, month, -i);
    days.push(prevDate);
  }
  
  // Días del mes actual
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }
  
  // Días del siguiente mes para llenar (hasta completar 42 días)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    days.push(new Date(year, month + 1, i));
  }
  
  return days;
};

const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  minDate = new Date(),
  error
}) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [currentMonth, setCurrentMonth] = useState(value || new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
  const [selectedHour, setSelectedHour] = useState(value?.getHours() || 9);
  const [selectedMinute, setSelectedMinute] = useState(value?.getMinutes() || 0);
  const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');
  const today = useMemo(() => new Date(), []);
  
  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ───────────────────────────────────────────────────────────────────────
  
  const calendarDays: CalendarDay[] = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const days = getDaysInMonth(year, month);
    
    return days.map(date => ({
      date,
      isCurrentMonth: date.getMonth() === month,
      isToday: isSameDay(date, today),
      isSelected: selectedDate ? isSameDay(date, selectedDate) : false,
      isPast: date < minDate && !isSameDay(date, minDate),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    }));
  }, [currentMonth, selectedDate, today, minDate]);
  
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = useMemo(() => Array.from({ length: 12 }, (_, i) => i * 5), []);
  
  const formattedDateTime = useMemo(() => {
    if (!selectedDate) return null;
    
    const dateStr = selectedDate.toLocaleDateString('es-CL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    const timeStr = `${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;
    
    return {
      date: dateStr.charAt(0).toUpperCase() + dateStr.slice(1),
      time: timeStr
    };
  }, [selectedDate, selectedHour, selectedMinute]);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };
  
  const handleDateSelect = (day: CalendarDay) => {
    if (day.isPast) return;
    setSelectedDate(day.date);
    setActiveTab('time');
    emitChange(day.date, selectedHour, selectedMinute);
  };
  
  const handleHourChange = (hour: number) => {
    setSelectedHour(hour);
    if (selectedDate) {
      emitChange(selectedDate, hour, selectedMinute);
    }
  };
  
  const handleMinuteChange = (minute: number) => {
    setSelectedMinute(minute);
    if (selectedDate) {
      emitChange(selectedDate, selectedHour, minute);
    }
  };
  
  const emitChange = (date: Date, hour: number, minute: number) => {
    const newDate = new Date(date);
    newDate.setHours(hour, minute, 0, 0);
    onChange(newDate);
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: CALENDAR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderCalendar = () => (
    <div style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={handlePrevMonth}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#2ca799',
            fontSize: '1.1rem',
            transition: 'all 0.2s ease',
            borderRadius: '8px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        
        <div style={{
          fontSize: '1.05rem',
          fontWeight: 600,
          color: 'var(--bs-body-color)'
        }}>
          {MONTHS_ES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        
        <button
          onClick={handleNextMonth}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#2ca799',
            fontSize: '1.1rem',
            transition: 'all 0.2s ease',
            borderRadius: '8px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      
      {/* Days header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px',
        marginBottom: '8px'
      }}>
        {DAYS_SHORT.map(day => (
          <div
            key={day}
            style={{
              textAlign: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--bs-secondary)',
              padding: '8px 0'
            }}
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px'
      }}>
        {calendarDays.map((day, idx) => {
          const isDisabled = day.isPast;
          const isSelected = day.isSelected;
          const isToday = day.isToday;
          
          return (
            <button
              key={idx}
              onClick={() => !isDisabled && handleDateSelect(day)}
              disabled={isDisabled}
              style={{
                aspectRatio: '1',
                border: isSelected ? '2px solid #2ca799' : 'none',
                borderRadius: '8px',
                background: isSelected
                  ? '#2ca799'
                  : isToday && !isSelected
                  ? 'rgba(44, 167, 153, 0.1)'
                  : 'transparent',
                color: isSelected
                  ? '#fff'
                  : isDisabled
                  ? 'var(--bs-secondary)'
                  : !day.isCurrentMonth
                  ? 'var(--bs-secondary)'
                  : 'var(--bs-body-color)',
                fontSize: '0.875rem',
                fontWeight: isToday || isSelected ? 600 : 400,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.3 : !day.isCurrentMonth ? 0.5 : 1,
                transition: 'all 0.2s ease',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                if (!isDisabled && !isSelected) {
                  e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDisabled && !isSelected) {
                  e.currentTarget.style.backgroundColor = isToday ? 'rgba(44, 167, 153, 0.1)' : 'transparent';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {day.date.getDate()}
              {isToday && !isSelected && (
                <div style={{
                  position: 'absolute',
                  bottom: '4px',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  backgroundColor: '#2ca799'
                }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TIME PICKER
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTimePicker = () => (
    <div style={{ padding: '24px 16px' }}>
      <div style={{
        display: 'flex',
        gap: '16px',
        justifyContent: 'center',
        marginBottom: '24px'
      }}>
        {/* Hour picker */}
        <div style={{ flex: 1, maxWidth: '120px' }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--bs-secondary)',
            marginBottom: '8px',
            textAlign: 'center'
          }}>
            Hora
          </div>
          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '8px',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            {hours.map(hour => (
              <button
                key={hour}
                onClick={() => handleHourChange(hour)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: 'none',
                  background: selectedHour === hour ? '#2ca799' : 'transparent',
                  color: selectedHour === hour ? '#fff' : 'var(--bs-body-color)',
                  fontSize: '0.95rem',
                  fontWeight: selectedHour === hour ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  if (selectedHour !== hour) {
                    e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedHour !== hour) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {String(hour).padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          fontSize: '1.5rem',
          fontWeight: 300,
          color: 'var(--bs-secondary)',
          marginTop: '28px'
        }}>
          :
        </div>
        
        {/* Minute picker */}
        <div style={{ flex: 1, maxWidth: '120px' }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--bs-secondary)',
            marginBottom: '8px',
            textAlign: 'center'
          }}>
            Minutos
          </div>
          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '8px',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            {minutes.map(minute => (
              <button
                key={minute}
                onClick={() => handleMinuteChange(minute)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: 'none',
                  background: selectedMinute === minute ? '#2ca799' : 'transparent',
                  color: selectedMinute === minute ? '#fff' : 'var(--bs-body-color)',
                  fontSize: '0.95rem',
                  fontWeight: selectedMinute === minute ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  if (selectedMinute !== minute) {
                    e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedMinute !== minute) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {String(minute).padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Selected time display */}
      {formattedDateTime && (
        <div style={{
          textAlign: 'center',
          padding: '16px',
          backgroundColor: 'rgba(44, 167, 153, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(44, 167, 153, 0.2)'
        }}>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 600,
            color: '#2ca799',
            marginBottom: '4px'
          }}>
            {formattedDateTime.time}
          </div>
          <div style={{
            fontSize: '0.813rem',
            color: 'var(--bs-secondary)'
          }}>
            {formattedDateTime.date}
          </div>
        </div>
      )}
    </div>
  );
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAIN
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div>
      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        padding: '4px',
        backgroundColor: 'var(--bs-body-bg)',
        borderRadius: '8px',
        border: '1px solid var(--bs-border-color)'
      }}>
        <button
          onClick={() => setActiveTab('date')}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            borderRadius: '6px',
            background: activeTab === 'date' ? '#2ca799' : 'transparent',
            color: activeTab === 'date' ? '#fff' : 'var(--bs-body-color)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faCalendarAlt} />
          Fecha
        </button>
        
        <button
          onClick={() => setActiveTab('time')}
          disabled={!selectedDate}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            borderRadius: '6px',
            background: activeTab === 'time' ? '#2ca799' : 'transparent',
            color: activeTab === 'time' ? '#fff' : 'var(--bs-body-color)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: selectedDate ? 'pointer' : 'not-allowed',
            opacity: selectedDate ? 1 : 0.5,
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faClock} />
          Hora
        </button>
      </div>
      
      {/* Content */}
      <Card style={{
        border: '1px solid var(--bs-border-color)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        backgroundColor: 'var(--bs-body-bg)',
        overflow: 'hidden'
      }}>
        {activeTab === 'date' ? renderCalendar() : renderTimePicker()}
      </Card>
      
      {/* Error */}
      {error && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          backgroundColor: 'rgba(242, 84, 91, 0.1)',
          border: '1px solid rgba(242, 84, 91, 0.3)',
          borderRadius: '8px',
          color: '#F2545B',
          fontSize: '0.813rem'
        }}>
          {error}
        </div>
      )}
      
      {/* Preview */}
      {formattedDateTime && !error && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: 'rgba(44, 167, 153, 0.05)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FontAwesomeIcon icon={faCheck} style={{ color: '#2ca799' }} />
          <div style={{ flex: 1, fontSize: '0.813rem', color: 'var(--bs-body-color)' }}>
            <strong>Programado para:</strong> {formattedDateTime.date} a las {formattedDateTime.time}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;